import { Box, useTheme } from '@mui/material'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import { GetSuperAdminProfileDrawerChildren } from '../../features/profileDrawer/getSuperAdminProfileDrawerChildren'
import useDebounce from '../../hooks/useDebounce'
import {
  changeProfileFilters,
  resetProfileBusinessListData,
  superAdminBusinessState,
  superAdminSwitchProfile,
  switchSuperAdminBusinessList,
} from '../../slices/superAdmin/businessSlice'
import { getLanguages } from '../../slices/translationSlice'
import { userState } from '../../slices/userSlice'
import AdminDrawer from './Drawer'
import { default as AdminHeader } from './header'

export default function SuperAdminLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const navigate = useNavigate()
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }
  const handleDrawerOnPopoverClick = () => {
    setMobileOpen(false)
  }
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()
  const userInformation = useAppSelector(userState)
  // eslint-disable-next-line camelcase
  const { userData, active_business_id } = userInformation

  const superAdminBusinessDetails = useAppSelector(superAdminBusinessState)
  const {
    completeProfileBusinessList,
    profileBusinessList,
    profileFilters,
    isLoadingProfileBusinessList,
  } = superAdminBusinessDetails
  const [searchValue, setSearchValue] = useState<string | number | undefined>('')
  const debouncedValue = useDebounce<string | number | undefined>(searchValue, 500)
  React.useEffect(() => {
    dispatch(getLanguages())
    if (profileFilters.page === 1) {
      dispatch(resetProfileBusinessListData())
    }
    dispatch(switchSuperAdminBusinessList(profileFilters))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, profileFilters])

  const handleSwitchProfile = (profileType: string, profileData?: { id: number }) => {
    setSearchValue('')
    let payload
    if (profileType === 'business' && profileData) {
      payload = {
        type: profileType,
        // eslint-disable-next-line camelcase
        business_id: profileData.id,
      }
    } else {
      payload = {
        type: profileType,
      }
    }
    dispatch(superAdminSwitchProfile(payload))
  }
  const handleConfigurationClick = (onClose: () => void) => {
    navigate('/super-admin/configuration')
    onClose()
  }

  const handleScroll = () => {
    dispatch(
      changeProfileFilters({
        ...profileFilters,
        page: profileFilters.page + 1,
      }),
    )
  }

  const handleSearch = (searchValue: string | number | undefined) => {
    dispatch(resetProfileBusinessListData())
    dispatch(
      changeProfileFilters({
        page: 1,
        search: searchValue,
      }),
    )
  }

  useEffect(() => {
    if (debouncedValue) {
      handleSearch(debouncedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  const clearSearchOnDrawerClose = React.useCallback(() => {
    setSearchValue('')
    dispatch(
      changeProfileFilters({
        search: '',
        page: 1,
      }),
    )
    dispatch(resetProfileBusinessListData())
  }, [dispatch])

  const profileDrawerChildren = GetSuperAdminProfileDrawerChildren({
    completeProfileBusinessList,
    profileBusinessList,
    profileFilters,
    handleSwitchProfile,
    handleConfigurationClick,
    handleScroll,
    isLoadingProfileBusinessList,
    userData,
    // eslint-disable-next-line camelcase
    active_business_id,
    theme,
    t,
    searchValue,
    setSearchValue,
    debouncedValue,
    handleSearch,
  })

  return (
    <>
      <AdminHeader
        handleDrawerOnPopoverClick={handleDrawerOnPopoverClick}
        handleDrawerToggle={handleDrawerToggle}
        userData={userData}
        onDrawerClose={clearSearchOnDrawerClose}
        profileDrawerChildren={profileDrawerChildren}
      >
        <Box component='nav'>
          <AdminDrawer
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
            userData={userData}
            handleDrawerOnPopoverClick={handleDrawerOnPopoverClick}
            onDrawerClose={clearSearchOnDrawerClose}
            profileDrawerChildren={profileDrawerChildren}
          />
        </Box>
      </AdminHeader>

      <Box sx={{ overflowX: 'hidden', overflowY: 'hidden' }}>
        <Outlet />
      </Box>
    </>
  )
}
